import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { Close, Edit } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { TestCaseCustomIcon } from "../../styled/CustomIcons";

const AddTestcaseDrawer = ({
  open,
  onClose,
  suite,
  testCases = [],
  onAddTestCases, // callback to update parent when new test cases are added
  onEdit, // open NewTestSuite / edit
}) => {
  const theme = useTheme();

  // Master state for all linked test cases in drawer
  const [linkedTestCases, setLinkedTestCases] = useState([]);
  // Temporary state for new test cases added via Add Test Case button
  const [addModeTestCases, setAddModeTestCases] = useState([]);

  // Sync drawer state with parent whenever it opens or testCases prop changes
  // useEffect(() => {
  //   if (open) {
  //     setLinkedTestCases(testCases);
  //     setAddModeTestCases([]); // reset temporary additions on open
  //   }
  // }, [open, testCases]);
  useEffect(() => {
    // Always sync linkedTestCases with parent testCases
    setLinkedTestCases(testCases);
  }, [testCases]);

  const hasTestCases = linkedTestCases.length > 0;

  // Add test cases from "Add Test Case" button in drawer
  const handleAddTestCases = (newCases) => {
    const merged = [...linkedTestCases, ...newCases];
    const unique = Array.from(
      new Map(merged.map((tc) => [tc.testCaseCustomId, tc])).values()
    );
    setLinkedTestCases(unique);
    setAddModeTestCases((prev) => [...prev, ...newCases]); // store temporary additions
    onAddTestCases(unique); // notify parent
  };

  // Handle edit (pencil) click - opens NewTestSuite / full edit
  const handleEditClick = () => {
    onEdit(linkedTestCases); // pass full current list to edit
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 420,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography fontWeight={600} fontSize="small">
            Linked Test Cases
          </Typography>

          <Box>
            {hasTestCases && (
              <IconButton
                onClick={handleEditClick}
                sx={{ color: theme.palette.primary.main }}
              >
                <Edit />
              </IconButton>
            )}
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: hasTestCases ? "flex-start" : "center",
            alignItems: hasTestCases ? "stretch" : "center",
          }}
        >
          {hasTestCases ? (
            <List
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {linkedTestCases.map((tc, index) => (
                <ListItem
                  key={tc.testCaseCustomId}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    display: "flex",
                    alignItems: "center", // align number and icon vertically
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  {/* Number + Icon container */}
                  <Box sx={{ display: "flex", alignItems: "center", mr: 1.5 }}>
                    {/* Index */}
                    <Box
                      sx={{
                        width: 20, // fixed width for alignment
                        textAlign: "right",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mr: 1, // small gap between number and icon
                      }}
                    >
                      {index + 1}.
                    </Box>

                    {/* Icon */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <TestCaseCustomIcon
                        sx={{ fontSize: 18, color: theme.palette.primary.main }}
                      />
                    </Box>
                  </Box>

                  {/* Text */}
                  <ListItemText
                    primary={`${tc.testCaseCustomId} - ${tc.testCaseTitle}`}
                    secondary={tc.folderPath}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                    secondaryTypographyProps={{
                      fontSize: 12,
                      color: theme.palette.text.secondary,
                      mt: 0.4,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 2,
                maxWidth: 300,
              }}
            >
              {/* Empty State */}
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.grey[100],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TestCaseCustomIcon
                  sx={{ fontSize: 28, color: theme.palette.primary.main }}
                />
              </Box>

              <Typography fontWeight={600}>No test cases linked</Typography>

              <Button
                fullWidth
                variant="contained"
                disabled={false} // you can use a condition here
                onClick={handleEditClick}
                startIcon={<Edit />}
                sx={{
                  mt: 1,
                  px: 3,
                  height: 40,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  background: `linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--emerald)))`,
                  color: "hsl(var(--primary-foreground))",
                  boxShadow: `0 4px 14px hsl(var(--cyan) / 0.35)`,
                  "&:hover": {
                    background: `linear-gradient(90deg, hsl(var(--cyan) / 0.9), hsl(var(--emerald) / 0.9))`,
                    boxShadow: `0 6px 20px hsl(var(--cyan) / 0.45)`,
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                }}
              >
                Add Test Case
              </Button>
            </Box>
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 2 }} />
      </Box>
    </Drawer>
  );
};

export default AddTestcaseDrawer;
